import type {
  ReportField,
  ReportingDataResponse,
} from '@risksmart-app/trpc/src/types';
import type { GetReportingDataQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { StrictMappedGraphQLSelection } from 'src/utils';

/**
 * V2 served reporting rows through a Hasura action Lambda that `JSON.stringify`d
 * the result, so every date/timestamptz cell reached the browser as an ISO
 * string. V3 is superjson end-to-end, so pg's native `Date` survives the wire
 * intact. The date display type hands the raw cell value to `toLocalDate`
 * (`src/utils/dateUtils.ts`), which returns a non-string argument unchanged, and
 * the table then renders it — "Objects are not valid as a React child".
 *
 * Restore the V2 contract in the hook layer: a `Date` becomes an ISO string,
 * everything else passes through untouched. `meta` gets the same treatment — its
 * columns are raw pg values too, so a date-typed meta column would arrive as a
 * `Date` for the same reason.
 */
export const toIsoIfDate = (value: unknown): unknown =>
  value instanceof Date ? value.toISOString() : value;

const normaliseMeta = (
  meta: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(meta).map(([key, value]) => [key, toIsoIfDate(value)])
  );

const normaliseField = (field: ReportField): ReportField => ({
  value: toIsoIfDate(field.value),
  meta: normaliseMeta(field.meta),
});

/**
 * Maps the `reportingData` tRPC response onto the legacy GraphQL query shape,
 * normalising `Date` values on the way through (see `toIsoIfDate`). Shared by
 * the eager (`useGetReportingData`) and lazy (`useLazyGetReportingData`) hooks
 * so the two cannot drift.
 */
export const mapReportingDataToGraphQL = (
  data: ReportingDataResponse
): StrictMappedGraphQLSelection<GetReportingDataQuery> => ({
  reportingData: data.map((row) => row.map(normaliseField)),
});
